<template>
	<BaseModal
		class="select-recipient-modal"
		modalTitle="Select Recipient"
		:showFooter="false"
		@close="handleClose"
	>
		<div slot="modalBody">
			<EmailSent
				v-if="emailSent"
				:message="response"
				@continue="handleClose"
			/>
			<FallBack
				v-else-if="brandBuilderSendEmailError"
				:message="brandBuilderSendEmailError"
				:border="false"
				iconStyle="fal"
				icon="exclamation-triangle"
			/>
			<SendAllConfirmation
				v-else-if="sendAllConfirmation"
				:selectAllMode="selectAllMode"
				:selectedLeadsCount="selectedLeadsCount"
				:success="success"
				:totalCount="totalCount"
				:waiting="waiting"
				@handleSendEmail="handleSendEmail"
			/>
			<ConfirmFilterChange
				v-else-if="filterChangeConfirmation"
				:success="success"
				:waiting="waiting"
				@confirmFilterChange="filterChange"
				@cancelFilterChange="cancelFilterChange"
			/>
			<div v-else>
				<div class="recipients-container">
					<div class="filters-container">
						<div class="broker-contact-filters">
							<OwnerFilter
								class="owner-menu"
								label
								caret
								v-if="canPerformProfileSelector"
								:brokerList="sortedBrokerShopUsers"
								:selectedBrokers="selectedBrokers"
								@onBrokerFilterChange="handleBrokerChange"
							/>
							<SingleFilter
								class="contact-type-filter filter"
								:type="contactFilterOptions.type"
								:options="contactFilterOptions.data"
								:selectedFilters="selectedFilters"
								debounced
								@onFilterChange="handleFilterChange"
							/>
						</div>
						<DebouncedSearchInput
							v-if="showSearch"
							class="search-bar"
							ariaLabel="Search for contacts"
							placeholder="Search..."
							@onSearchInputChange="handleSearchInputChange"
							:value="searchTerm"
						/>
					</div>
					<div v-if="showError">
						<FallBack
							iconStyle="fal"
							icon="exclamation-triangle"
							message="We could not retrieve your contacts, please try again later. "
						/>
					</div>
					<SelectionStatus
						:leadIdsCount="selectedLeadsCount"
						:selectAllMode="selectAllMode"
						:totalCount="totalCount"
						@updateSelectAllMode="handleSelectAllMode"
					/>
					<LoadingIcon v-if="isLoading" alignment="center" />
					<div v-if="showNoResults">
						<FallBack
							iconStyle="fal"
							icon="exclamation-triangle"
							message="Sorry, there are no results."
						/>
					</div>
					<Table
						v-if="showTable"
						class="table"
						:aria-label="'contacts list'"
					>
						<RecipientTableHeader
							:currentPageContacts="currentPageContacts"
							:selectAllMode="selectAllMode"
						/>
						<RecipientTableBody
							:currentPageContacts="currentPageContacts"
							:selectAllMode="selectAllMode"
						/>
					</Table>
					<Pagination
						v-if="showPagination"
						@onPageChange="handlePageNumberChange"
						@onPageSizeChange="handlePageSizeChange"
						:totalCount="totalCount"
						:pageNumber="pageNumber"
						:pageSize="pageSize"
					/>
				</div>
				<OverviewModalFooter
					:success="success"
					:waiting="waiting"
					:isSaveModal="true"
					@handleClick="handleSendAllEmail"
					@close="handleClose"
					primaryButtonName="Send"
					:primaryButtonDisabled="isSendDisabled"
				/>
			</div>
		</div>
	</BaseModal>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { FILTER_TYPE } from './constants';

import BaseModal from '@/components/global/BaseModal.vue';
import EmailSent from './EmailSent.vue';
import SendAllConfirmation from './SendAllConfirmation.vue';
import ConfirmFilterChange from './ConfirmFilterChange.vue';
import DebouncedSearchInput from '@/components/global/DebouncedSearchInput.vue';
import OwnerFilter from '@/components/brand-builder/OwnerFilter.vue';
import SingleFilter from '@/components/global/SingleFilter.vue';
import FallBack from '@/components/global/FallBack.vue';
import SelectionStatus from '@/components/global/SelectionStatus.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import Table from '@/components/global/Table.vue';
import RecipientTableHeader from './RecipientTableHeader.vue';
import RecipientTableBody from './RecipientTableBody.vue';
import Pagination from '@/components/global/Pagination';
import OverviewModalFooter from '@/views/client-connect-v2/modals/OverviewModalFooter.vue';
import { scrollToTop } from '@/views/client-connect-v2/helpers';

export default {
	name: 'SelectRecipientModal',
	props: {
		response: String,
		brandBuilderSendEmailError: String,
		success: Boolean,
		waiting: Boolean,
		emailSent: Boolean,
	},
	components: {
		BaseModal,
		EmailSent,
		SendAllConfirmation,
		ConfirmFilterChange,
		DebouncedSearchInput,
		OwnerFilter,
		SingleFilter,
		FallBack,
		SelectionStatus,
		LoadingIcon,
		Table,
		RecipientTableHeader,
		RecipientTableBody,
		Pagination,
		OverviewModalFooter,
	},
	data() {
		return {
			newBrokers: [],
			newFilters: [],
			newSearch: '',
			currentFilter: '',
			selectAllMode: false,
		};
	},
	computed: {
		...mapState({
			// bb-select-recipient-modal store
			contacts: (state) => state.bbSelectRecipientModal.contacts,
			error: (state) => state.bbSelectRecipientModal.error,
			filterChangeConfirmation: (state) =>
				state.bbSelectRecipientModal.filterChangeConfirmation,
			isLoading: (state) => state.bbSelectRecipientModal.isLoading,
			leadIds: (state) => state.bbSelectRecipientModal.leadIds,
			pageNumber: (state) => state.bbSelectRecipientModal.pageNumber,
			pageSize: (state) => state.bbSelectRecipientModal.pageSize,
			searchTerm: (state) => state.bbSelectRecipientModal.searchTerm,
			selectedBrokers: (state) =>
				state.bbSelectRecipientModal.selectedBrokers,
			selectedFilters: (state) =>
				state.bbSelectRecipientModal.selectedFilters,
			sendAllConfirmation: (state) =>
				state.bbSelectRecipientModal.sendAllConfirmation,
			totalCount: (state) => state.bbSelectRecipientModal.totalCount,
			// profile-switcher store
			canPerformProfileSelector: (state) =>
				state.profileSwitcher.mainProfileSelector
					.canPerformProfileSelector,
		}),
		...mapGetters([
			'contactFilterOptions',
			'isSendDisabled',
			'showTable',
			'showSearch',
			'showPagination',
			'showNoResults',
			'showError',
			'sortedBrokerShopUsers',
		]),
		currentPageContacts() {
			return this.contacts.slice(
				(this.pageNumber - 1) * this.pageSize,
				this.pageNumber * this.pageSize,
			);
		},
		selectedLeadsCount() {
			return this.leadIds.length;
		},
		isSendDisabled() {
			if (this.selectAllMode) {
				return false;
			} else {
				return isEmpty(this.leadIds);
			}
		},
	},
	methods: {
		...mapActions([
			'resetSelectRecipientModal',
			'setSearchTerm',
			'setPageNumber',
			'setPageSize',
			'clearContacts',
			'setSelectedBrokers',
			'setSelectedFilters',
			'setFilterChangeConfirmation',
			'setSendAllConfirmation',
			'getBBLeads',
			'getAllLeadTypes',
			'setLeadIds',
		]),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		handleClose() {
			this.$emit('close');
		},
		handlePageNumberChange(page) {
			this.setPageNumber(page);
		},
		handlePageSizeChange(size) {
			if (this.pageSize !== size) {
				this.setPageSize(size);
				this.setPageNumber(1);
				scrollToTop();
			}
		},
		handleSelectAllMode(selectAllMode) {
			this.selectAllMode = selectAllMode;
			this.setLeadIds([]);
		},
		handleSendEmail() {
			const recipients = this.contacts
				.filter((contact) => this.leadIds.includes(contact.leadId))
				.map((contact) => ({
					leadId: contact.leadId,
					contactEmailAddress: contact.email,
					contactFirstName: contact.firstName,
					contactLastName: contact.lastName,
				}));
			this.$emit('onSend', {
				recipients,
				sendAll: this.selectAllMode,
				contactsIds: this.selectedBrokers,
				typesIds: this.selectedFilters,
				searchTerm: this.searchTerm,
			});
		},
		handleSendAllEmail() {
			if (this.selectAllMode) {
				this.setSendAllConfirmation(true);
			} else {
				this.handleSendEmail();
			}
		},
		handleBrokerChange(selectedBrokerIds) {
			this.currentFilter = FILTER_TYPE.BROKER;
			this.newBrokers = [...selectedBrokerIds];
			if (!this.leadIds.length && !this.selectAllMode) {
				this.filterChange();
			} else {
				this.setFilterChangeConfirmation(true);
			}
		},
		handleFilterChange({ filter }) {
			this.currentFilter = FILTER_TYPE.CONTACT_TYPE;
			this.newFilters = [...filter];
			if (!this.leadIds.length && !this.selectAllMode) {
				this.filterChange();
			} else {
				this.setFilterChangeConfirmation(true);
			}
		},
		handleSearchInputChange(searchTerm) {
			this.currentFilter = FILTER_TYPE.SEARCH;
			this.newSearch = searchTerm;
			if (!this.leadIds.length && !this.selectAllMode) {
				this.filterChange();
			} else {
				this.setFilterChangeConfirmation(true);
			}
		},
		filterChange() {
			switch (this.currentFilter) {
				case FILTER_TYPE.BROKER:
					this.setSelectedBrokers([...this.newBrokers]);
					break;
				case FILTER_TYPE.CONTACT_TYPE:
					this.setSelectedFilters([...this.newFilters]);
					break;
				case FILTER_TYPE.SEARCH:
					this.setSearchTerm(this.newSearch);
					break;
			}

			this.refreshContacts();

			this.setFilterChangeConfirmation(false);

			this.setLeadIds([]);
			this.selectAllMode = false;
			this.clearComponentFilters();
		},
		cancelFilterChange() {
			this.setFilterChangeConfirmation(false);
			this.clearComponentFilters();
		},
		clearComponentFilters() {
			this.newFilters = [];
			this.newBrokers = [];
			this.newSearch = '';
			this.currentFilter = '';
		},
		async refreshContacts() {
			this.setPageNumber(1);
			if (this.showNoResults) {
				await this.getBBLeads();
			} else {
				/**
				 * when contacts are cleared they will be loaded
				 * again by the currentPageContacts watcher
				 */
				this.clearContacts();
			}
		},
	},
	watch: {
		async currentPageContacts(value) {
			if (this.error) return;

			if (
				(isEmpty(value) || value.length !== this.pageSize) &&
				this.totalCount > this.contacts.length
			) {
				await this.getBBLeads();
			}
		},
		leadIds() {
			if (
				this.leadIds.length === this.totalCount &&
				this.totalCount !== 0
			) {
				this.selectAllMode = !this.selectAllMode;
				this.setLeadIds([]);
			}
		},
	},
	async created() {
		this.resetSelectRecipientModal();
		await this.getBBLeads();
		await this.getAllLeadTypes();
	},
	destroyed() {
		this.resetSelectRecipientModal();
	},
};
</script>

<style lang="scss" scoped>
.select-recipient-modal {
	/deep/ .modal-dialog {
		top: 90px;
		max-width: 45rem;
	}

	.recipients-container {
		@include rem('margin', 20px);

		.broker-contact-filters {
			display: flex;
			justify-content: space-between;
			@include rem('gap', 20px);
			align-items: flex-start;
			@include rem('margin-bottom', 20px);

			.owner-menu {
				flex-grow: 1;
			}
		}

		.search-bar {
			flex-grow: 1;
		}

		.table {
			margin-top: 15px;

			.table-head-row,
			.table-body-row {
				display: grid;
				grid-template-columns: 4% 40% 52%;
				gap: 2%;
				@include rem('padding', 12px);
			}

			.table-body-row {
				@include rem('font-size', 16px);
			}

			@media only screen and (max-width: 600px) {
				.table-head-row,
				.table-body-row {
					gap: 4%;
					grid-template-columns: 5% auto;
				}

				.table-head__email,
				.table-body__email {
					display: none;
				}
			}
		}

		/deep/ .pagination-nav {
			@include rem('padding', 8px 0);
		}

		/deep/ .page-size-dropdown {
			@include rem('margin', 8px 15px);
		}
	}

	.button-container {
		display: flex;
		justify-content: flex-end;
		@include rem('padding', 10px 20px);
		border-top: 1px solid $border-color-light;
		.cancel-button,
		.send-button {
			width: 20%;
			@include rem('margin-left', 10px);
		}
	}
}
</style>